'use client'

import { ApiResponse } from 'src/types/api.types'
import { useFetch } from '@hooks'
import type {
  Currency,
  CurrencyOption,
  FeaturePlanInfoWithType,
  PricingApiResponse,
} from '@query/page-query/pricing-query/pricing.model'

type FetchHookOptions = {
  onSuccess?: () => void
  onFailure?: (error: unknown) => void
  /** Auto-fetch on mount */
  autoFetch?: boolean
  params?: Partial<{
    currency: Currency
    include_features: boolean
  }>
}

/**
 * Fetches features from the Next.js API route
 */
async function fetchFeaturesFromApi(): Promise<FeaturePlanInfoWithType> {
  const response = await fetch('/api/v1/pricing/features')
  const json: ApiResponse<FeaturePlanInfoWithType> = await response.json()

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch features')
  }

  return json.data
}

/**
 * Fetches plans from the Next.js API route
 */

type Args = {
  params?: FetchHookOptions['params']
}

async function fetchPlansFromApi({
  params,
}: Args): Promise<PricingApiResponse> {
  const finalParams = new URLSearchParams({
    currency: params?.currency || 'USD',
    include_features: params?.include_features?.toString() || 'false',
  })
  const response = await fetch(`/api/v1/pricing/plans?${finalParams}`)
  const json: ApiResponse<PricingApiResponse> = await response.json()

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch plans')
  }

  return json.data
}

/**
 * Fetches currencies from the Next.js API route
 */
async function fetchCurrenciesFromApi(): Promise<CurrencyOption[]> {
  const response = await fetch('/api/v1/pricing/currencies')
  const json: ApiResponse<CurrencyOption[]> = await response.json()

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch currencies')
  }

  return json.data
}

/**
 * Hook to fetch pricing features from the API
 *
 * @example
 * ```tsx
 * const { fetchFeatures, features, isLoading } = usePricingFeatures({
 *   autoFetch: true,
 *   onSuccess: () => console.log('Features loaded!'),
 * })
 * ```
 */
export const usePricingFeatures = ({
  onSuccess,
  onFailure,
  autoFetch = false,
}: FetchHookOptions = {}) => {
  const {
    fetch: fetchFeatures,
    data,
    isLoading,
    isError,
    isSuccess,
    reset,
  } = useFetch<Record<string, never>, FeaturePlanInfoWithType>({
    asyncFn: fetchFeaturesFromApi,
    onSuccess: onSuccess ? () => onSuccess() : undefined,
    onFailure,
    autoFetch,
  })

  return {
    fetchFeatures: () => fetchFeatures({}),
    features: data,
    isLoading,
    isError,
    isSuccess,
    reset,
  }
}

/**
 * Hook to fetch pricing plans from the API
 *
 * @example
 * ```tsx
 * const { fetchPlans, plans, isLoading } = usePricingPlans({
 *   autoFetch: true,
 *   onSuccess: () => console.log('Plans loaded!'),
 * })
 * ```
 */
export const usePricingPlans = ({
  onSuccess,
  onFailure,
  autoFetch = false,
  params,
}: FetchHookOptions = {}) => {
  const {
    fetch: fetchPlans,
    data,
    isLoading,
    isError,
    isSuccess,
    reset,
  } = useFetch<Args, PricingApiResponse>({
    asyncFn: fetchPlansFromApi,
    onSuccess: onSuccess ? () => onSuccess() : undefined,
    onFailure,
    autoFetch,
    initialData: { params },
  })

  return {
    fetchPlans: (
      fetchParams?: Partial<{ currency: Currency; include_features: boolean }>
    ) => fetchPlans({ params: fetchParams || params }),
    plans: data,
    isLoading,
    isError,
    isSuccess,
    reset,
  }
}

/**
 * Hook to fetch available currencies from the API
 *
 * @example
 * ```tsx
 * const { fetchCurrencies, currencies, isLoading } = useCurrencies({
 *   autoFetch: true,
 *   onSuccess: () => console.log('Currencies loaded!'),
 * })
 * ```
 */
export const useCurrencies = ({
  onSuccess,
  onFailure,
  autoFetch = false,
}: Omit<FetchHookOptions, 'params'> = {}) => {
  const {
    fetch: fetchCurrencies,
    data,
    isLoading,
    isError,
    isSuccess,
    reset,
  } = useFetch<Record<string, never>, CurrencyOption[]>({
    asyncFn: fetchCurrenciesFromApi,
    onSuccess: onSuccess ? () => onSuccess() : undefined,
    onFailure,
    autoFetch,
  })

  return {
    fetchCurrencies: () => fetchCurrencies({}),
    currencies: data,
    isLoading,
    isError,
    isSuccess,
    reset,
  }
}

/**
 * Hook to fetch both features and plans together
 * Useful when you need all pricing data at once
 *
 * @example
 * ```tsx
 * const { fetchAll, features, plans, isLoading } = usePricingData({
 *   autoFetch: true,
 * })
 * ```
 */
export const usePricingData = ({
  onSuccess,
  onFailure,
  autoFetch = false,
}: FetchHookOptions = {}) => {
  const features = usePricingFeatures({ onFailure, autoFetch })
  const plans = usePricingPlans({ onFailure, autoFetch })

  const fetchAll = async () => {
    await Promise.all([features.fetchFeatures(), plans.fetchPlans()])
    onSuccess?.()
  }

  return {
    fetchAll,
    features: features.features,
    plans: plans.plans,
    isLoading: features.isLoading || plans.isLoading,
    isError: features.isError || plans.isError,
    isSuccess: features.isSuccess && plans.isSuccess,
  }
}
